import { RotateCcw } from 'lucide-react';
import type { ShareRow } from '#shared/types';
import { Button } from '#client/components/ui/Button';
import type { Column } from '#client/components/ui/DataTable';
import { ShareStatusBadge } from '#client/components/ui/StatusBadge';
import { formatDateTime } from '#client/lib/format';
import { sentBy } from './sent-by';

/**
 * The fixed columns of the share history.
 *
 * At module scope, like every other column array in the app, so it is not
 * reallocated on every render. The retry column depends on state and is built
 * by `retryColumn`.
 */
export const shareHistoryColumns: Column<ShareRow>[] = [
  {
    key: 'created_at',
    header: 'When',
    render: (row) => formatDateTime(row.created_at),
  },
  {
    key: 'to_e164',
    header: 'To',
    render: (row) => <span className="tnum">+{row.to_e164}</span>,
  },
  {
    key: 'report_names',
    header: 'Tests',
    render: (row) => (
      <>
        {row.report_names}
        {row.report_ids.length < row.report_total_at_send && (
          <span className="c-muted">
            {' '}
            ({row.report_ids.length} of {row.report_total_at_send})
          </span>
        )}
      </>
    ),
  },
  {
    key: 'status',
    header: 'Status',
    render: (row) => (
      <>
        <ShareStatusBadge status={row.status} />
        {row.attempt_no > 1 && <span className="c-muted"> attempt {row.attempt_no}</span>}
      </>
    ),
  },
  {
    key: 'shared_by',
    header: 'Sent by',
    render: sentBy,
  },
];

interface RetryColumnOptions {
  canRetry: boolean;
  retryingId: number | null;
  onRetry: (share: ShareRow) => void;
}

export function retryColumn({
  canRetry,
  retryingId,
  onRetry,
}: RetryColumnOptions): Column<ShareRow> {
  return {
    key: 'retry',
    header: '',
    align: 'end',
    render: (row) =>
      row.status === 'failed' && row.retryable && canRetry ? (
        <Button
          variant="secondary"
          size="sm"
          icon={<RotateCcw />}
          loading={retryingId === row.id}
          onClick={() => onRetry(row)}
        >
          Retry
        </Button>
      ) : null,
  };
}
